/**
 * Psychological Flexibility Diagnostic - Results Page (v7.0.0)
 *
 * Displays wellbeing-focused results with:
 * - Psychological inflexibility assessment
 * - Top 3 inflexibility patterns requiring attention
 * - Strengths (best-performing patterns)
 * - ACT Hexaflex reference guide
 * - Evidence-based recommendations
 *
 * IMPORTANT DIFFERENCES FROM OTHER DIAGNOSTICS:
 * - NO transformation value calculations
 * - NO persona baselines
 * - NO financial metrics (£M)
 * - NO zone theming (Foundational/Stretch/Mastery)
 * - Focus entirely on wellbeing and psychological flexibility
 *
 * Uses unified CSS design system (17 modular files)
 * Connected to Algorithm v2.0 calculations
 *
 * Copyright © 2026 Daring Futures. All rights reserved.
 * Proprietary and confidential.
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getHighestInflexibilityAreas } from '../../utils/algorithms';
import DiagnosticHeader from '../../components/diagnostics/DiagnosticHeader';

// Import unified CSS
import '../../styles/diagnostics/index.css';

function PsychologicalFlexibilityResults() {
  const { code } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [results, setResults] = useState(null);

  // Fetch results from API
  useEffect(() => {
    const fetchResults = async () => {
      if (!code) {
        setError('No diagnostic code provided');
        setLoading(false);
        return;
      }

      try {
        const response = await fetch(`/api/get-psychological-flexibility-results?code=${code}`);
        const data = await response.json();

        if (data.success && data.results) {
          // Check if assessment is completed
          if (!data.results.completedAt) {
            setError('This assessment has not been completed yet. Please complete the assessment first.');
            setLoading(false);
            return;
          }

          setResults(data.results);
        } else {
          setError(data.message || 'Assessment not found');
        }
      } catch (err) {
        console.error('Error fetching results:', err);
        setError('Failed to load results. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchResults();
  }, [code]);

  // Get risk level class for badge
  const getRiskClass = (burdenLevel) => {
    if (!burdenLevel) return 'moderate';
    const level = burdenLevel.toLowerCase();
    if (level === 'low') return 'low';
    if (level === 'moderate') return 'moderate';
    if (level === 'high') return 'high';
    if (level === 'critical') return 'critical';
    return 'moderate';
  };

  // Get risk interpretation text
  const getRiskInterpretation = (averageScore) => {
    if (averageScore >= 7.0) {
      return 'Critical: Significant psychological inflexibility. Urgent therapeutic support strongly recommended.';
    } else if (averageScore >= 4.0) {
      return 'High to Moderate: Notable difficulty with adaptive responses. Proactive intervention recommended.';
    } else if (averageScore >= 2.0) {
      return 'Moderate to Low: Some capacity for adaptive responses, with room for growth.';
    } else {
      return 'Low: Strong capacity for adaptive, values-based responses to challenges.';
    }
  };

  // Get top 3 best patterns (strengths)
  const getTop3Strengths = () => {
    if (!results?.scores) return [];

    const patterns = [
      { id: 1, name: 'Mind Wandering', description: 'Present moment awareness' },
      { id: 2, name: 'Thought Entanglement', description: 'Cognitive defusion' },
      { id: 3, name: 'Emotional Avoidance', description: 'Acceptance' },
      { id: 4, name: 'Self-Criticism', description: 'Self-as-context' },
      { id: 5, name: 'Values Disconnection', description: 'Values clarity' },
      { id: 6, name: 'Inaction', description: 'Committed action' },
      { id: 7, name: 'Rigidity', description: 'Psychological flexibility' },
      { id: 8, name: 'Experiential Avoidance', description: 'Willingness' },
      { id: 9, name: 'Cognitive Fusion', description: 'Thought flexibility' },
      { id: 10, name: 'Behavioral Inflexibility', description: 'Behavioral flexibility' }
    ];

    const scoredPatterns = patterns.map((pattern, index) => ({
      ...pattern,
      score: results.scores[`flex${index + 1}`] || 5
    }));

    // Sort by score ASCENDING (lowest score = least frequent problem = strength)
    return scoredPatterns.sort((a, b) => a.score - b.score).slice(0, 3);
  };

  // Get pattern questions for display
  const getPatternQuestion = (patternId) => {
    const questions = [
      'How often does your mind drift away from what you\'re doing?',
      'How often do you get caught up in unhelpful thoughts or worries?',
      'How often do you try to suppress or avoid difficult emotions?',
      'How often do you engage in harsh self-judgment or self-criticism?',
      'How often do you feel unclear about what truly matters to you?',
      'How often do you avoid taking action because it feels too hard or uncomfortable?',
      'How often do you get stuck in rigid patterns of thinking or behaving?',
      'How often do you avoid uncomfortable situations or experiences?',
      'How often do you treat your thoughts as absolute truths rather than just thoughts?',
      'How often do you persist with unhelpful behaviors even when they\'re not working?'
    ];
    return questions[patternId - 1] || '';
  };

  // Get ACT process mapping
  const getACTProcess = (patternName) => {
    const mapping = {
      'Present Moment Awareness': 'Present Moment',
      'Cognitive Defusion': 'Cognitive Defusion',
      'Acceptance': 'Acceptance',
      'Self-as-Context': 'Self-as-Context',
      'Values Clarity': 'Values',
      'Committed Action': 'Committed Action',
      'Psychological Flexibility': 'Hexaflex Integration',
      'Experiential Avoidance': 'Acceptance (Willingness)',
      'Cognitive Fusion': 'Cognitive Defusion',
      'Inaction': 'Committed Action'
    };
    return mapping[patternName] || 'ACT Process';
  };

  // Loading state
  if (loading) {
    return (
      <div className="container" style={{ textAlign: 'center', padding: '80px 16px' }}>
        <div style={{ fontSize: '18px', color: '#697A90' }}>Loading your results...</div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="container" style={{ textAlign: 'center', padding: '80px 16px' }}>
        <div style={{ fontSize: '18px', color: '#dc2626', marginBottom: '24px' }}>{error}</div>
        <button onClick={() => navigate('/diagnostics/psychological-flexibility')} className="btn-primary">
          Start New Diagnostic
        </button>
      </div>
    );
  }

  // Main content
  const top3Inflexibility = getHighestInflexibilityAreas(results.scores);
  const top3Strengths = getTop3Strengths();
  const riskClass = getRiskClass(results.burdenLevel);

  return (
    <>
      <DiagnosticHeader />
      <div className="container">
        {/* Header */}
        <div className="card">
          <div className="badge">STEP 3 OF 3 - RESULTS</div>
          <h1>Your Psychological Inflexibility Assessment</h1>
          <p style={{ color: '#697A90', marginBottom: '16px' }}>
            Wellbeing diagnostic results for <strong>{results.leaderName || 'Leader'}</strong>
          </p>
          <p style={{ color: '#697A90', fontSize: '14px', fontStyle: 'italic' }}>
            This is a standalone wellbeing diagnostic with no transformation value calculations, no persona baselines, and no financial metrics.
          </p>
        </div>

        {/* Key Metrics Card */}
        <div className="card">
          <h2>Your Psychological Flexibility Profile</h2>

          <div style={{ marginBottom: '24px', textAlign: 'center' }}>
            <div className={`risk-badge ${riskClass}`}>
              {results.burdenLevel} Risk
            </div>
          </div>

          <div className="metric-grid">
            <div className="metric-card primary">
              <div className="metric-label">Psychological Inflexibility Factor</div>
              <div className="metric-value">{(results.psychologicalInflexibilityFactor * 100).toFixed(1)}%</div>
              <div className="metric-sublabel">Overall inflexibility score</div>
            </div>

            <div className="metric-card warning">
              <div className="metric-label">Wellbeing Burden Score</div>
              <div className="metric-value">{results.averageScore.toFixed(1)}/10</div>
              <div className="metric-sublabel">Average pattern frequency</div>
            </div>
          </div>

          <div className="info-box blue" style={{ marginTop: '24px' }}>
            <p><strong>What Your Scores Mean:</strong></p>
            <p style={{ marginTop: '8px' }}>
              Your <strong>Psychological Inflexibility Factor of {(results.psychologicalInflexibilityFactor * 100).toFixed(1)}%</strong> indicates
              the overall frequency of inflexibility patterns in your life. Higher percentages suggest more frequent experiences of psychological rigidity.
            </p>
            <p style={{ marginTop: '12px' }}>
              Your <strong>Wellbeing Burden Score of {results.averageScore.toFixed(1)}/10</strong> represents the average frequency across all 10 inflexibility patterns.
              This score helps identify the overall impact on your psychological flexibility and wellbeing.
            </p>
          </div>

          <div className="info-box yellow" style={{ marginTop: '16px' }}>
            <p><strong>Risk Level Interpretation:</strong></p>
            <p style={{ marginTop: '8px' }}>{getRiskInterpretation(results.averageScore)}</p>
            <p style={{ marginTop: '12px' }}>
              <strong>Threshold Guide:</strong><br />
              • Low (0-1.9): Rare inflexibility patterns, strong adaptive capacity<br />
              • Moderate (2.0-3.9): Occasional patterns, good capacity for growth<br />
              • High (4.0-6.9): Frequent patterns, proactive intervention recommended<br />
              • Critical (7.0-10): Persistent patterns, urgent support recommended
            </p>
          </div>
        </div>

        {/* Top 3 Inflexibility Patterns */}
        <div className="card">
          <h2>Top 3 Inflexibility Patterns Requiring Attention</h2>
          <p style={{ color: '#697A90', marginBottom: '16px' }}>
            These patterns have the highest scores and represent the most frequent experiences of psychological inflexibility in your life.
          </p>

          <div className="pattern-list">
            {top3Inflexibility.map((pattern, index) => (
              <div key={pattern.id} className="pattern-item">
                <div className="pattern-header">
                  <div className="pattern-title">{index + 1}. {pattern.name}</div>
                  <div className="pattern-score">{pattern.score}/10</div>
                </div>
                <div className="pattern-description">
                  {getPatternQuestion(pattern.id)}
                </div>
                <div style={{ marginTop: '12px', marginBottom: '12px' }}>
                  <strong style={{ color: '#0EA5E9' }}>ACT Process:</strong> {getACTProcess(pattern.name)}
                </div>
                <div className="pattern-recommendation">
                  <p><strong>Recommended Intervention:</strong></p>
                  <p style={{ marginTop: '8px' }}>{pattern.intervention}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Strengths Box */}
          <div className="info-box green" style={{ marginTop: '24px' }}>
            <p><strong>Your Strengths:</strong></p>
            <p style={{ marginTop: '12px' }}>
              These are your top 3 best-performing patterns (lowest frequency = least frequent problems = strengths):
            </p>
            {top3Strengths.map((strength, index) => (
              <p key={strength.id} style={{ marginTop: '8px' }}>
                • <strong>{strength.name}</strong> ({strength.score}/10): You demonstrate strength in {strength.description},
                experiencing this pattern infrequently. This is a foundation to build upon.
              </p>
            ))}
          </div>
        </div>

        {/* ACT Hexaflex Reference */}
        <div className="card">
          <div className="hexaflex-reference">
            <h3>ACT Hexaflex: The 6 Core Processes of Psychological Flexibility</h3>
            <p style={{ fontSize: '14px', color: '#475569', marginBottom: '16px' }}>
              Acceptance and Commitment Therapy (ACT) identifies six core processes that build psychological flexibility.
              Understanding these processes helps you develop the skills to respond more effectively to life's challenges.
            </p>

            <div className="hexaflex-grid">
              <div className="hexaflex-item">
                <h4>1. Acceptance</h4>
                <p>
                  Opening up and making room for difficult feelings, sensations, and experiences without trying to suppress or avoid them.
                </p>
              </div>

              <div className="hexaflex-item">
                <h4>2. Cognitive Defusion</h4>
                <p>
                  Creating distance from unhelpful thoughts by seeing them as just thoughts, not absolute truths or commands you must obey.
                </p>
              </div>

              <div className="hexaflex-item">
                <h4>3. Present Moment</h4>
                <p>
                  Bringing your attention fully to the here and now with openness and curiosity, rather than getting lost in thoughts about the past or future.
                </p>
              </div>

              <div className="hexaflex-item">
                <h4>4. Self-as-Context</h4>
                <p>
                  Developing awareness of yourself as more than your thoughts, feelings, and roles—the observing self that experiences life.
                </p>
              </div>

              <div className="hexaflex-item">
                <h4>5. Values</h4>
                <p>
                  Clarifying what truly matters to you and what you want to stand for in life—your chosen life directions.
                </p>
              </div>

              <div className="hexaflex-item">
                <h4>6. Committed Action</h4>
                <p>
                  Taking effective action guided by your values, even when difficult thoughts and feelings show up.
                </p>
              </div>
            </div>

            <p style={{ fontSize: '13px', color: '#475569', marginTop: '16px', fontStyle: 'italic' }}>
              Working with these six processes together builds psychological flexibility—the ability to stay present and engaged
              with your values even when life gets challenging.
            </p>
          </div>
        </div>

        {/* Recommendations */}
        <div className="card">
          <h2>Recommended Next Steps</h2>
          <p style={{ color: '#697A90', marginBottom: '16px' }}>
            <strong>{results.leaderName || 'Leader'}</strong>, based on your assessment, here are evidence-based recommendations
            to build psychological flexibility and improve wellbeing:
          </p>

          <div className="recommendation-list">
            {results.calculations?.recommendations?.map((rec, index) => (
              <div key={index} className="recommendation-item">
                <h4>{index + 1}. {rec.split(':')[0]}</h4>
                <p>{rec.split(':').slice(1).join(':').trim()}</p>
              </div>
            )) || (
              <>
                <div className="recommendation-item">
                  <h4>1. Focus on Your Highest-Impact Patterns</h4>
                  <p>
                    Start with the top 3 inflexibility patterns identified above. These represent the areas where building
                    flexibility skills will have the greatest impact on your wellbeing. Choose one pattern to focus on first,
                    and practice the recommended interventions consistently for 2-4 weeks before moving to the next.
                  </p>
                </div>

                <div className="recommendation-item">
                  <h4>2. Build a Daily Mindfulness Practice</h4>
                  <p>
                    Present moment awareness is foundational to all six ACT processes. Start with just 5-10 minutes daily
                    of mindfulness meditation or mindful breathing. This builds your capacity to notice thoughts and feelings
                    without getting caught up in them, creating space for more flexible responses.
                  </p>
                </div>

                <div className="recommendation-item">
                  <h4>3. Clarify Your Core Values</h4>
                  <p>
                    Complete a formal values clarification exercise to identify what truly matters to you across different
                    life domains (relationships, work, health, personal growth, etc.). Use a values card sort or bull's-eye
                    exercise. When you're clear on your values, it's easier to take committed action even when it's uncomfortable.
                  </p>
                </div>

                <div className="recommendation-item">
                  <h4>4. Practice Cognitive Defusion Techniques</h4>
                  <p>
                    Learn to create distance from unhelpful thoughts using defusion techniques like "I'm having the thought that...",
                    thought labeling, or metaphor work. This helps reduce the power of self-critical or anxious thoughts,
                    allowing you to choose your responses rather than reacting automatically.
                  </p>
                </div>

                <div className="recommendation-item">
                  <h4>5. Consider ACT-Based Coaching or Therapy</h4>
                  <p>
                    If your scores indicate moderate to high inflexibility, consider working with an ACT-trained therapist or coach.
                    Research shows ACT has an 8% effect size on mental wellbeing outcomes (Skews, 2018). Professional support
                    can accelerate your progress and provide personalized guidance for your specific patterns.
                  </p>
                </div>
              </>
            )}
          </div>

          <div className="info-box orange" style={{ marginTop: '24px' }}>
            <p><strong>Evidence Base:</strong></p>
            <p style={{ marginTop: '8px' }}>
              These recommendations are grounded in Acceptance and Commitment Therapy (ACT), which has demonstrated
              an 8% effect size (η² = 0.08) in improving psychological flexibility and mental wellbeing across multiple
              randomized controlled trials.
            </p>
            <p style={{ marginTop: '8px' }}>
              <em>Source: Skews, S. J., 2018, "Acceptance and commitment coaching: A meta-analysis"</em>
            </p>
          </div>
        </div>

        {/* CTA Section */}
        <div className="cta-section">
          <div className="cta-question">
            Ready to build your psychological flexibility?<br />
            Let's discuss your path to greater wellbeing
          </div>
          <div className="cta-subtext">
            Book a Truth Session to explore evidence-based strategies for your specific inflexibility patterns
          </div>
          <p style={{ marginTop: '24px', marginBottom: '20px', opacity: '0.9' }}>
            Schedule a call to discuss your results and create a personalized plan to build psychological flexibility.
            We'll help you understand your patterns, identify the most effective interventions, and develop practical
            strategies for improving your wellbeing and effectiveness.
          </p>
          <button onClick={() => window.open('https://calendly.com/andrewkidd/truth-session', '_blank')} className="btn-truth-session">
            Book Truth Session
          </button>
        </div>

        {/* Actions */}
        <div className="card">
          <div className="button-group">
            <button className="btn-secondary" onClick={() => window.print()}>Print Results</button>
            <button className="btn-primary" onClick={() => navigate('/request-diagnostic')}>
              Request Another Diagnostic
            </button>
          </div>
        </div>

        {/* Footer */}
        <div style={{ textAlign: 'center', color: '#9ca3af', fontSize: '14px', marginTop: '32px' }}>
          <p>Daring Futures • Psychological Flexibility Diagnostic • v7.0.0 • Step 3 of 3</p>
        </div>
      </div>
    </>
  );
}

export default PsychologicalFlexibilityResults;
